import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  Leaf, Recycle, Heart, Users, Shield, Scale, Award, Globe,
  CheckCircle, Droplets, TreePine
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import SEO from "../components/SEO";

const RSEPage = () => {
  const { t } = useLanguage();

  const ecoValues = [
    { icon: Leaf, title: t('responsabilite'), description: t('responsabiliteDesc') },
    { icon: Droplets, title: t('produitsVertsTitle'), description: t('produitsVertsDesc') },
    { icon: Recycle, title: t('gestionRaisonnee'), description: t('gestionRaisonneeDesc') },
    { icon: TreePine, title: t('engagementDurable'), description: t('engagementDurableDesc') }
  ];

  const socialCommitments = [
    {
      icon: Users, title: t('entrepriseJuste'), description: t('entrepriseJusteDesc'),
      points: [t('rsePoint1_1'), t('rsePoint1_2'), t('rsePoint1_3'), t('rsePoint1_4')]
    },
    {
      icon: Shield, title: t('integrite'), description: t('integriteDesc'),
      points: [t('rsePoint2_1'), t('rsePoint2_2'), t('rsePoint2_3'), t('rsePoint2_4')]
    },
    {
      icon: Scale, title: t('concurrenceLoyale'), description: t('concurrenceLoyaleDesc'),
      points: [t('rsePoint3_1'), t('rsePoint3_2'), t('rsePoint3_3'), t('rsePoint3_4')]
    },
    {
      icon: Heart, title: t('engagementSocietal'), description: t('engagementSocietalDesc'),
      points: [t('rsePoint4_1'), t('rsePoint4_2'), t('rsePoint4_3'), t('rsePoint4_4')]
    }
  ];

  const certifications = [
    { title: t('equipementsEconomes'), description: t('equipementsEconomesDesc') },
    { title: t('gestionDechets'), description: t('gestionDechetsDesc') },
    { title: t('formationContinue'), description: t('formationContinueDesc') }
  ];

  return (
    <div data-testid="rse-page" className="pt-20">
      <SEO
        title="Engagement RSE - Nettoyage écologique"
        description="Découvrez l'engagement RSE de Nettis Facility : produits écologiques certifiés, gestion responsable des déchets, respect des employés. Nettoyage professionnel éco-responsable en Belgique."
        keywords="nettoyage écologique, RSE nettoyage, produits écologiques nettoyage, développement durable entreprise nettoyage, Ecolabel Belgique"
        canonicalUrl="https://www.nettis.be/engagement-rse"
      />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 right-20 w-72 h-72 bg-[#10B981] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-emerald-300 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#10B981]/20 text-[#10B981] text-sm font-medium rounded-full mb-6">
                <Leaf className="w-4 h-4" />{t('engagementRse')}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-6">
                {t('rsePageTitle')} <span className="text-[#10B981]">{t('ecoResponsable')}</span>
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">{t('rseDescription')}</p>
              <Link to="/services">
                <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3 font-semibold shadow-lg shadow-green-900/20 transition-transform duration-200 hover:-translate-y-0.5">
                  {t('decouvrirServices')}
                </Button>
              </Link>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1731119415638-a19158e0fef0" alt="Engagement écologique" className="rounded-2xl shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Eco Values Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#10B981] uppercase tracking-widest mb-4 block">{t('valeursEcologiques')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">{t('quatrePiliers')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('piliersDec')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {ecoValues.map((value, index) => (
              <div key={value.title} className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border border-green-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-16 h-16 rounded-xl bg-[#10B981] flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3">{value.title}</h3>
                <p className="text-slate-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-20 bg-[#0F172A]">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">{t('engagementsConcrets')}</h2>
            <p className="text-slate-400">{t('engagementsConcretsDesc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div key={cert.title} className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <div className="w-12 h-12 rounded-lg bg-[#10B981]/20 flex items-center justify-center mb-4">
                  <Award className="w-6 h-6 text-[#10B981]" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{cert.title}</h3>
                <p className="text-slate-400 text-sm">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Commitments */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-[#1E3A8A] uppercase tracking-widest mb-4 block">{t('responsabiliteSociale')}</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">{t('engagementsSociaux')}</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">{t('engagementsSociauxDesc')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {socialCommitments.map((commitment, index) => (
              <div key={commitment.title} className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-lg transition-shadow">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-[#1E3A8A]/10 flex items-center justify-center shrink-0">
                    <commitment.icon className="w-7 h-7 text-[#1E3A8A]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0F172A] mb-2">{commitment.title}</h3>
                    <p className="text-slate-600">{commitment.description}</p>
                  </div>
                </div>
                <ul className="space-y-2">
                  {commitment.points.map((point, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle className="w-4 h-4 text-[#10B981] shrink-0" />
                      <span className="text-sm text-slate-600">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Commitments */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-slate-50 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-[#1E3A8A] flex items-center justify-center">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A]">{t('confidentialite')}</h3>
              </div>
              <p className="text-slate-600 mb-4">{t('confidentialiteDesc')}</p>
              <ul className="space-y-2">
                {[t('rseConfid1'), t('rseConfid2'), t('rseConfid3'), t('rseConfid4')].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />{item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-50 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 rounded-lg bg-[#10B981] flex items-center justify-center">
                  <Globe className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#0F172A]">{t('proprieteIntellectuelle')}</h3>
              </div>
              <p className="text-slate-600 mb-4">{t('proprieteIntellectuelleDesc')}</p>
              <ul className="space-y-2">
                {[t('rsePropInt1'), t('rsePropInt2'), t('rsePropInt3'), t('rsePropInt4')].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                    <CheckCircle className="w-4 h-4 text-[#10B981]" />{item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <Leaf className="w-16 h-16 text-[#10B981] mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4">{t('rejoignezDemarche')}</h2>
          <p className="text-lg text-slate-600 mb-8 max-w-2xl mx-auto">{t('rejoignezDemarcheDesc')}</p>
          <Link to="/contact">
            <Button className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-3 font-semibold shadow-lg shadow-green-900/20 transition-transform duration-200 hover:-translate-y-0.5">
              {t('nousContacter')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default RSEPage;
